import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { TabInduk, TabL1A, TabL1B, TabGrouped, TabL2A, TabL3BA, TabL1D, TabL3A2 } from "@/components/spt/SptTabs";
import { TabL1E } from "@/components/spt/BuktiPotongTab";
import { CekKesiapan } from "@/components/spt/CekKesiapan";

const TAB_META = {
  induk: { code: "Induk", title: "Ringkasan SPT (PTKP, PPh Terutang & Status)" },
  l1a: { code: "L-1 A", title: "Harta pada Akhir Tahun Pajak" },
  l1b: { code: "L-1 B", title: "Utang pada Akhir Tahun Pajak" },
  l1d: { code: "L-1 D", title: "Penghasilan Neto Dalam Negeri dari Pekerjaan" },
  l1e: { code: "L-1 E", title: "Daftar Bukti Pemotongan/Pemungutan PPh" },
  l2a: { code: "L-2 A", title: "Penghasilan yang Dikenakan PPh Bersifat Final" },
  l2b: { code: "L-2 B", title: "Penghasilan yang Tidak Termasuk Objek Pajak" },
  l3a2: { code: "L-3A-2", title: "Rekonsiliasi Laporan Keuangan" },
  l3b_a: { code: "L-3B A", title: "Rekapitulasi Peredaran Bruto (PPh Final)" },
};
const ORDER = ["induk", "l1a", "l1b", "l1d", "l1e", "l2a", "l2b", "l3a2", "l3b_a"];
const thisYear = new Date().getFullYear();

function TabBody({ id, data, tahun, reload }) {
  const d = data.data[id];
  if (id === "induk") return <TabInduk d={d}/>;
  if (id === "l1a") return <TabL1A d={d}/>;
  if (id === "l1b") return <TabL1B d={d}/>;
  if (id === "l2b") return <TabGrouped d={d} id="l2b" head={["Jenis Penghasilan", "Penghasilan Bruto"]} totalLabel="Total Bukan Objek Pajak"
    empty="Belum ada pemasukan dengan perlakuan Bukan Objek Pajak (mis. hibah, warisan)."/>;
  if (id === "l1d") return <TabL1D d={d}/>;
  if (id === "l1e") return <TabL1E d={d} tahun={tahun} reload={reload}/>;
  if (id === "l2a") return <TabL2A d={d}/>;
  if (id === "l3b_a") return <TabL3BA d={d}/>;
  return <TabL3A2 d={d}/>;
}

export default function AsistenSptCoretax() {
  const [tahun, setTahun] = useState(thisYear - 1);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("induk");
  const load = useCallback(() => {
    api.get("/spt-coretax", { params: { tahun } }).then((r) => {
      setData(r.data);
      setTab((t) => (r.data.tabs.includes(t) ? t : "induk"));
    }).catch((e) => toast.error(formatApiError(e)));
  }, [tahun]);
  useEffect(() => { load(); }, [load]);
  const tabs = data ? ORDER.filter((t) => data.tabs.includes(t)) : [];

  return (
    <div className="space-y-5" data-testid="spt-coretax-page">
      <PageHeader title="Asisten SPT Coretax" subtitle="Angka akhir per lampiran SPT 1770. Klik Salin lalu tempel ke portal Coretax DJP."
        actions={<Select value={String(tahun)} onValueChange={(v) => setTahun(Number(v))}>
          <SelectTrigger className="w-32" data-testid="spt-tahun"><SelectValue/></SelectTrigger>
          <SelectContent>{[0, 1, 2, 3].map((i) => <SelectItem key={i} value={String(thisYear - i)}>Tahun {thisYear - i}</SelectItem>)}</SelectContent>
        </Select>}/>
      <CekKesiapan tahun={tahun} spt={data}/>
      <div className="flex gap-1 overflow-x-auto no-scrollbar border-b border-slate-200" role="tablist" data-testid="spt-tabs">
        {tabs.map((t) => (
          <button key={t} role="tab" onClick={() => setTab(t)} data-testid={`spt-tab-${t}`}
            className={`shrink-0 px-3.5 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t ? "border-[#166db4] text-[#166db4]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {TAB_META[t].code}
          </button>
        ))}
      </div>
      {data && tabs.includes(tab) && (
        <section className="space-y-3" data-testid={`spt-panel-${tab}`}>
          <h2 className="text-base md:text-lg font-semibold text-slate-800">{TAB_META[tab].code}. {TAB_META[tab].title}</h2>
          <TabBody id={tab} data={data} tahun={tahun} reload={load}/>
        </section>
      )}
      {data && tabs.length === 4 && (
        <p className="text-[12px] text-slate-500" data-testid="spt-modul-hint">Lampiran lain (PPh Final UMKM, PPh 21, Pembukuan) muncul setelah modulnya diaktifkan oleh Super Admin.</p>
      )}
    </div>
  );
}
